import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksNavSelectEvent } from '../../events/blocks-nav-select';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/chevron-down';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksSideMenu } from '../side-menu/side-menu';
import type { SideMenuItemOrientation } from './side-menu-item.model';
import styles from './side-menu-item.styles';

/**
 * @deprecated - This component `cat-side-menu-item` has been deprecated and the updated version will be available with name `cat-side-menu-item-labs`. New component will be found under labs folder from Minor Release 3.10.0, `cat-side-menu-item` will be removed in 5.0 major release.
 * @slot items - adds children content inside an item
 * @slot before - The component before content (generally used to display icon)
 * @slot after - The component after content (generally used to display icons/badge)
 * @slot default - displays the text
 * @fires bl-sidemenuitem-select - emitted along with `isMobile` prop when side menu item is selected
 * @csspart base - The component's base wrapper.
 * @csspart inner - The component's inner.
 * @csspart link - The component's link element.
 * @csspart icon - The component's icon.
 * @csspart part-before - The component's before slot.
 * @csspart text - The component's text element.
 * @csspart part-default - The component's default slot.
 * @csspart right - The component's right  element.
 * @csspart after-wrap - The component's after wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart icon-down - The component's `<cat-icon-chevron-down>` icon.
 * @csspart panel - The component's panel.
 * @csspart part-items - The component's items slot.
 */
export class BlocksSideMenuItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Orientation
   * - **vertical** renders the icon on top of the text and narrows the sidebar
   */
  @property({ type: String })
  orientation: SideMenuItemOrientation;

  /**
   * Append to the class name. Used for passing in utility classes
   */
  @property({ type: String })
  styleModifier = '';

  /**
   * Active state of the item
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Current state of the location in the product
   */
  @property({ type: Boolean })
  isCurrent: boolean | undefined;

  /**
   * Disable state of the item
   */
  @property({ type: Boolean })
  isDisabled: boolean | undefined;

  /**
   * Collapsed state of the menu
   */
  @property({ type: Boolean })
  isCollapsed: boolean | undefined;

  /**
   * Role for the side-menu-item
   * treeitem for tree view and listitem for other
   */
  @property({ type: String })
  sideMenuItemRole = 'listitem';

  @state()
  isMobile = false;

  public slots = new SlotController(this, 'items', 'before', 'after', null);

  /**
   * Connected callback lifecycle
   * 1) Add event listeners
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside only if isCollpased id true.
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active navigation item
   * 5) If the navigation is active and we've clicked outside of the nav then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    if (this.isCollapsed) {
      /* 2 */
      if (!this.isActive) {
        return;
      }

      /* 3 */
      if (!this.shadowRoot?.host) {
        throw Error('Could not determine navigation context during click handler');
      }

      /* 4 */
      const didClickInside = event.composedPath().includes(this.shadowRoot.host);

      /* 5 */
      if (this.isActive && !didClickInside) {
        this.isActive = false;
      }
    }
  };

  /**
   * Toggle active state of side menu item
   * 1) Remove isActive state from all sibling elements
   * 2) Toggle active state of element selected
   */
  private clickHandler = (e: MouseEvent) => {
    e.preventDefault();
    if (this.parentNode) {
      const sideMenuItems = this.parentNode.querySelectorAll('cat-side-menu-item');
      sideMenuItems.forEach((element) => {
        if (element !== this) {
          element.isActive = false; /* 1 */
        }
      });
    }
    this.isActive = !this.isActive; /* 2 */
  };

  /**
   * Click on list item without dropdown
   * 1) On click, remove all isCurrent properties from all side-menu items
   * 2) Add isCurrent to the item clicked
   */
  private linkClickHandler(evt: MouseEvent) {
    //Allow event to propagate
    setTimeout(() => {
      if (evt.defaultPrevented) {
        return;
      }
      const navItems = this.parentNode?.querySelectorAll('cat-side-menu-item');

      navItems?.forEach((element) => {
        if (element !== this) {
          element.isCurrent = false; /* 1 */
        }

        const allListItems = element.querySelectorAll('cat-list-item');
        allListItems.forEach((item) => {
          if (item.isCurrent === true) {
            item.isCurrent = false; /* 1 */
          }
        });
      });

      this.isCurrent = true; /* 2 */
      if (this.isMobile) {
        this.dispatch({ eventName: 'bl-sidemenuitem-select', detailObj: { isMobile: this.isMobile } }) as BlocksNavSelectEvent;
      }
    });
  }

  /**
   * List item selected from the side menu item item panel
   * 1) Get the event target. If that is the `cat-list-item` or the parent is (for spans inside),
   * then remove all isCurrent states from nav items
   * 2) Set the selected item to true
   * 3) If the item has children, then remove all `isCurrent` states from those items if another item is selected
   * 4) If nested children within dropdown, find all parent node list items and set `isCurrent` if that item is selected.
   */
  private itemSelected(evt: MouseEvent | KeyboardEvent) {
    const subListElement = evt.target as BlocksListItem;
    const allSideMenuItems = this.parentNode?.querySelectorAll('cat-side-menu-item');

    if (
      ((subListElement.outerHTML.includes('cat-list-item') && !subListElement.querySelector('cat-list')) ||
        (subListElement.parentElement?.outerHTML.includes('cat-list-item') && !subListElement.parentElement.querySelector('cat-list'))) &&
      !subListElement.disabled &&
      !subListElement.querySelector('cat-list')
    ) {
      //Allow event to propagate
      setTimeout(() => {
        if (evt.defaultPrevented) {
          return;
        }
        const navItems = this.parentNode?.querySelectorAll('cat-side-menu-item');
        navItems?.forEach((element) => {
          /* 1 */
          if (element !== this) {
            element.isCurrent = false;
          }

          allSideMenuItems?.forEach((element) => {
            const allListItems = element.querySelectorAll('cat-list-item');

            allListItems.forEach((item) => {
              if (item.isCurrent === true) {
                item.isCurrent = false; /* 3 */
              }
            });
          });
        });

        this.isCurrent = true; /* 2 */

        if (subListElement.closest('cat-list-item')) {
          const selectedItem = subListElement.closest('cat-list-item');
          if (selectedItem) {
            selectedItem.isCurrent = true;
            let newSubListElement = selectedItem;

            while (newSubListElement.parentElement?.nodeName !== 'CAT-SIDE-MENU-ITEM') {
              newSubListElement = <BlocksListItem>newSubListElement.parentElement;

              if (newSubListElement.nodeName === 'CAT-LIST-ITEM') {
                newSubListElement.isCurrent = true; /* 4 */
              }
            }
          }
        }

        const thisParent = this.parentElement;
        if ((<BlocksSideMenu>thisParent).isCollapsed) {
          this.isActive = false;
          const itemToFocus = this.shadowRoot?.querySelector('.cat-c-side-menu__link') as HTMLElement;
          itemToFocus?.focus();
        }
        if (this.isMobile) {
          this.dispatch({ eventName: 'bl-sidemenuitem-select', detailObj: { isMobile: this.isMobile } }) as BlocksNavSelectEvent;
        }
      });
    }
  }

  /**
   * Handle keydown event for side menu item
   * 1) If the item is disabled, prevent default
   * 2) If the item is active and enter key is pressed, select the item
   */
  private handleSideNavItemKeyDown(event: KeyboardEvent) {
    if (this.isDisabled && event.key !== KEYS.TAB) {
      event.preventDefault();
    }

    if (this.isActive) {
      if (event.key === KEYS.ENTER) {
        /*2*/
        this.itemSelected(event);
      }
    }
  }

  render(): TemplateResult<1> {
    const hasItemsSlot = this.slots.hasSlotted('items');
    const componentClassNames = this.componentClassNames('cat-c-side-menu__item', {
      'cat-is-collapsed': this.isCollapsed === true,
      'cat-is-active': this.isActive === true,
      'cat-is-current': this.isCurrent === true,
      'cat-is-disabled': this.isDisabled === true,
      'cat-c-side-menu__item--vertical': this.orientation === 'vertical',
      'cat-is-mobile': this.isMobile === true,
      'cat-c-side-menu__item-has-before': this.slots.hasSlotted('before'),
      'cat-c-side-menu__item-has-after': this.slots.hasSlotted('after'),
      'cat-c-side-menu__item-has-items': hasItemsSlot
    });

    return html`
      <li
        part="base"
        .role=${ifDefined(this.sideMenuItemRole === 'listitem' ? this.sideMenuItemRole : 'none')}
        class="${componentClassNames}"
        @keydown=${this.handleSideNavItemKeyDown}
      >
        <div part="inner" class="cat-c-side-menu__item-inner">
          ${html`
                <button
                part="link" 
                  class="cat-c-side-menu__link"
                  aria-disabled=${ifDefined(this.isDisabled)}
                  aria-selected=${ifDefined(this.isDisabled ? 'false' : undefined)}
                  aria-current=${ifDefined(!this.isDisabled && this.isCurrent ? 'true' : undefined)}
                  @click=${hasItemsSlot ? this.clickHandler : this.linkClickHandler}
                  aria-expanded=${ifDefined(hasItemsSlot ? (this.isActive === true ? true : false) : undefined)}
                  .role=${ifDefined(this.sideMenuItemRole === 'listitem' ? undefined : this.sideMenuItemRole)}
                >
                  <div part="icon" class="cat-c-side-menu__icon">
                    <slot part="part-before" name="before"></slot>
                  </div>
                  <div part="text" class="cat-c-side-menu__text">
                    <slot part="part-default"></slot>
                  </div>

                  <div part="right" class="cat-c-side-menu__item-right"></div>
                  <div part="after-wrap" class="cat-c-side-menu__after">
                    <slot part="part-after" name="after"></slot>
                  </div>
                  <cat-icon-chevron-down
                    part="icon-down"
                    exportparts=${this.getExportParts('chevron-down', 'icon')}
                    class="cat-c-side-menu__items-icon"
                  ></cat-icon-chevron-down>
          </div>
                </button>
          `}
        </div>
        <div part="panel" class="cat-c-side-menu__item-panel">
          <slot part="part-items" name="items" @click=${this.itemSelected}></slot>
        </div>
      </li>
    `;
  }
}

if (customElements.get('cat-side-menu-item') === undefined) {
  customElements.define('cat-side-menu-item', BlocksSideMenuItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-side-menu-item': BlocksSideMenuItem;
  }
}
